/**
 * MarketingInstellingenPage — frequency caps, blackout, approval-drempel,
 * reply-venster en verzenddomein-status.
 */
import { useCallback, useEffect, useState } from 'react';
import { marketingFetch } from '../../stores/marketing-store';
import { MarketingNav, MarketingPageHeader, SectionCard, formatDateTime } from './shared';

interface SettingRow {
  value: unknown;
  updated_at: string;
  updated_by: string | null;
}

type Settings = Record<string, SettingRow>;

export function MarketingInstellingenPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Bewerkbare staat
  const [maxWeek, setMaxWeek] = useState(2);
  const [maxMonth, setMaxMonth] = useState(6);
  const [blackoutStart, setBlackoutStart] = useState('22:00');
  const [blackoutEnd, setBlackoutEnd] = useState('07:00');
  const [approvalThreshold, setApprovalThreshold] = useState(100);
  const [replyDays, setReplyDays] = useState(14);

  const load = useCallback(async () => {
    try {
      const data = await marketingFetch<{ settings: Settings }>('/api/marketing/settings');
      setSettings(data.settings);
      const caps = data.settings.frequency_caps?.value as { max_per_week?: number; max_per_month?: number } | undefined;
      if (caps) {
        setMaxWeek(caps.max_per_week ?? 2);
        setMaxMonth(caps.max_per_month ?? 6);
      }
      const blackout = data.settings.blackout?.value as { start?: string; end?: string } | undefined;
      if (blackout) {
        setBlackoutStart(blackout.start ?? '22:00');
        setBlackoutEnd(blackout.end ?? '07:00');
      }
      const threshold = data.settings.approval_threshold?.value;
      if (typeof threshold === 'number') setApprovalThreshold(threshold);
      const reply = data.settings.reply_attribution_days?.value;
      if (typeof reply === 'number') setReplyDays(reply);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (key: string, value: unknown) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await marketingFetch('/api/marketing/settings', { method: 'PATCH', body: { key, value } });
      setNotice('Opgeslagen.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  const sendDomain = settings?.send_domain?.value as { domain?: string; from?: string; reply_to?: string } | undefined;
  const lastSync = settings?.last_sync?.value as { at?: string } | undefined;
  const inputCls = 'border border-gray-300 rounded-lg px-3 py-2 text-sm w-28';

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader title="Instellingen" subtitle="Guardrails voor alle marketing-verzending" />
        <MarketingNav />

        <div className="p-6 space-y-6 max-w-2xl">
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
          {notice && <div className="bg-emerald-50 text-emerald-700 text-sm rounded-lg px-4 py-3">{notice}</div>}

          <SectionCard title="Frequency caps">
            <p className="text-xs text-gray-400 mb-3">Maximum aantal marketing-mails per contact. Elke send checkt dit — ook journeys.</p>
            <div className="flex items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Per week</label>
                <input type="number" min={1} value={maxWeek} onChange={(e) => setMaxWeek(parseInt(e.target.value, 10) || 1)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Per maand</label>
                <input type="number" min={1} value={maxMonth} onChange={(e) => setMaxMonth(parseInt(e.target.value, 10) || 1)} className={inputCls} />
              </div>
              <button
                onClick={() => save('frequency_caps', { max_per_week: maxWeek, max_per_month: maxMonth })}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Opslaan
              </button>
            </div>
          </SectionCard>

          <SectionCard title="Blackout-venster">
            <p className="text-xs text-gray-400 mb-3">Geen verzending in dit venster (Europe/Amsterdam) — de queue wacht tot het venster sluit.</p>
            <div className="flex items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Van</label>
                <input type="time" value={blackoutStart} onChange={(e) => setBlackoutStart(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Tot</label>
                <input type="time" value={blackoutEnd} onChange={(e) => setBlackoutEnd(e.target.value)} className={inputCls} />
              </div>
              <button
                onClick={() => save('blackout', { start: blackoutStart, end: blackoutEnd, tz: 'Europe/Amsterdam' })}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Opslaan
              </button>
            </div>
          </SectionCard>

          <SectionCard title="Goedkeuring & replies">
            <div className="flex items-end gap-4 flex-wrap">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Goedkeuring vereist boven (ontvangers)</label>
                <input
                  type="number"
                  min={0}
                  value={approvalThreshold}
                  onChange={(e) => setApprovalThreshold(parseInt(e.target.value, 10) || 0)}
                  className={inputCls}
                />
              </div>
              <button
                onClick={() => save('approval_threshold', approvalThreshold)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Opslaan
              </button>
            </div>
            <div className="flex items-end gap-4 mt-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Reply-attributie (dagen)</label>
                <input type="number" min={1} max={90} value={replyDays} onChange={(e) => setReplyDays(parseInt(e.target.value, 10) || 14)} className={inputCls} />
              </div>
              <button
                onClick={() => save('reply_attribution_days', replyDays)}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Opslaan
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Een klant-reply binnen dit venster stopt automatisch de campagne/journey die de mail stuurde.
            </p>
          </SectionCard>

          <SectionCard title="Verzenddomein">
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="text-gray-400">Domein:</span> <span className="font-medium text-gray-900">{sendDomain?.domain ?? '—'}</span>
              </p>
              <p>
                <span className="text-gray-400">Afzender:</span> <span className="font-medium text-gray-900">{sendDomain?.from ?? '—'}</span>
              </p>
              <p>
                <span className="text-gray-400">Reply-To:</span> <span className="font-medium text-gray-900">{sendDomain?.reply_to ?? '—'}</span>
              </p>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Reply-To is bewust info@kleda.nl — die inbox wordt gesynct en is het detectiemechanisme voor automatische
              reply-suppressie. Domein-verificatie beheer je in het Resend-dashboard (EU-West-1).
            </p>
          </SectionCard>

          <SectionCard title="Contactsync">
            <p className="text-sm text-gray-600">
              Laatste sync: <span className="font-medium text-gray-900">{formatDateTime(lastSync?.at)}</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">Nachtelijk om 02:30 (Vercel cron) — Salesforce → Supabase, incl. segment-materialisatie.</p>
          </SectionCard>
        </div>
      </div>
    </>
  );
}
